import * as React from "react";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { orderByIds } from "@racoon/lib";
import {
  Button,
  FilterBlock,
  IconButton,
  Skeleton,
  cn,
  useTableExport,
  useToast,
} from "@racoon/ui";
import { IcCalendar, IcDragAndDrop, IcRightChevron, IcSettings } from "@racoon/icons";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import {
  useCreateShiftPlanMutation,
  useDeleteShiftPlanMutation,
  useListShiftScheduleQuery,
} from "~/shared/api/shift-plan/shift-plan.grpc";
import { NO_TEAM, groupByTeam, useDriverTeams } from "~/shared/lib/driver-teams";
import { replaceTables } from "~/shared/hooks/useTableSettings";
import { SortableList } from "~/shared/ui/sortable-list";
import { useTeamModal } from "~/pages/settings/components/teams-page/use-team-modal";
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
} from "~/shared/api/organization/organization.grpc";
import { MonthTotal, ScheduleCell, StagedEdit, planFill } from "../components/schedule-cell";
import { countsInTotal, monthTotal } from "../model/month-total";

// Geometry from the Figma frame 15292:31406: driver panel 332, day cell 40,
// month-total column 68. The panel header is 120 = nav 64 + gap 16 + date row
// 40, which is what keeps the three columns on the same row grid.
// The driver panel is the only elastic column: day cells and the totals column
// keep their design widths, so a wider - or zoomed-out - window lengthens the
// names instead of stretching the grid. 332 is the design width and stays the
// floor; below it the matrix shrinks and scrolls.
const NAME_COL = "min-w-[332px] flex-1";
const TOTAL_COL = "w-[68px]";
const ROW_H = 40;
// Day cell side — must stay in step with ScheduleCell's size-10.
const DAY_COL = 40;

/**
 * One unsaved cell edit. Both ids are converted where they enter the draft: a
 * list response arrives through connectBaseQuery, whose normalizeBigInt turns
 * every bigint into a decimal string while the generated type still says
 * bigint — so passing one straight back to a mutation hands the Connect client
 * a string that TypeScript cannot catch.
 */
type StagedChange =
  | { kind: "plan"; driverId: bigint; date: string; planType: ConnectProto.ShiftPlanType }
  | { kind: "delete"; shiftPlanId: bigint };

const CellState = ConnectProto.ShiftCellState;
const PlanType = ConnectProto.ShiftPlanType;

/**
 * The plan types, in the order the picker shows them. A click paints the cell
 * with whichever one is picked, so the order is presentation only.
 */
const PLAN_TYPES = [PlanType.DAY, PlanType.NIGHT, PlanType.DAY_OFF] as const;

/**
 * The cell only needs to know what to paint, so the draft entry is narrowed to
 * that: which plan is pending, or that the plan is going away.
 */
const stagedEditOf = (change?: StagedChange): StagedEdit | undefined => {
  if (!change) return undefined;
  return change.kind === "plan" ? { kind: "plan", planType: change.planType } : { kind: "delete" };
};

const EXPORT_STATE_KEY: Record<number, string> = {
  [CellState.ENDED]: "export.ended",
  [CellState.ACTIVE]: "export.active",
  [CellState.NOT_STARTED]: "export.notStarted",
  [CellState.PLANNED]: "export.planned",
  [CellState.MISSED]: "export.missed",
};

const PLAN_LEGEND_KEY: Record<number, string> = {
  [PlanType.DAY]: "legend.day",
  [PlanType.NIGHT]: "legend.night",
  [PlanType.DAY_OFF]: "legend.dayOff",
};

/** A planned day exports by its type; the state alone says only "planned". */
const EXPORT_PLAN_KEY: Record<number, string> = {
  [PlanType.DAY]: "export.planned",
  [PlanType.NIGHT]: "export.night",
  [PlanType.DAY_OFF]: "export.dayOff",
};

/** A driver row. */
type Entry = { key: string; row: ConnectProto.DriverShiftScheduleRow; number: number };

/** A team's rows under its heading; with no teams at all, one group without a heading. */
type Group = { key: string; label?: string; team?: ConnectProto.Team; entries: Entry[] };

/**
 * The drivers' order lives in the organization settings, beside the tables'
 * column layouts: one "table" per team whose columns are driver ids, plus one for
 * the "no team" group. Each team keeps its own order, so moving a driver in one
 * team leaves that driver's place in the other teams alone.
 */
const DRIVER_ORDER_TABLE = "shift_schedule_drivers";
const orderTable = (groupKey: string) =>
  groupKey ? `${DRIVER_ORDER_TABLE}:${groupKey}` : DRIVER_ORDER_TABLE;

const driverIdOf = (row: ConnectProto.DriverShiftScheduleRow) => String(row.driverId);

type DriverRowProps = { sortId: string; driverId: string; number: number; name: string };

/** A driver row: number, drag handle, name. */
const DriverRow: React.FC<DriverRowProps> = ({ sortId, driverId, number, name }) => {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId,
  });
  return (
    <div
      ref={setNodeRef}
      data-testid="schedule-driver"
      data-driver={driverId}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "group/row h-10 shrink-0 overflow-hidden rounded-lg data-[hovered]:bg-slate-100",
        isDragging && "relative z-10 bg-slate-100",
      )}
    >
      <div className="flex h-full items-center">
        <span className="flex h-full w-10 shrink-0 items-center justify-center px-2 text-center text-sm font-normal leading-4 tracking-[-0.01px] text-text-subtle">
          {number}
        </span>
        <button
          type="button"
          data-testid="schedule-driver-handle"
          aria-label={t(Namespace.ShiftSchedule, "dragToReorder")}
          {...attributes}
          {...listeners}
          className={cn(
            "flex h-full w-8 shrink-0 cursor-grab touch-none items-center justify-center active:cursor-grabbing",
            isDragging
              ? "text-[#151a24]"
              : "text-slate-400 group-data-[hovered]/row:text-[#151a24]",
          )}
        >
          {/* The icon's dots sit in a 20px box of its 24px viewBox: 24px here matches the design's 20px handle. */}
          <IcDragAndDrop className="size-6" />
        </button>
        <span className="min-w-0 flex-1 truncate p-2 text-sm font-normal leading-4 tracking-[-0.01px] text-text-body">
          {name}
        </span>
      </div>
    </div>
  );
};

const pad = (n: number) => String(n).padStart(2, "0");
const dateStr = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;

const monthLabel = (year: number, month: number, locale: string) => {
  const name = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(locale, {
    month: "long",
    timeZone: "UTC",
  });
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
};

const shiftMonth = (year: number, month: number, delta: number) => {
  const d = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
};

const ShiftSchedulePage = () => {
  const t = useT();
  const { lang } = useLang();
  const { toast } = useToast();
  const { activeCompanyId } = useAuthSession();

  const [{ year, month }, setMonth] = React.useState(() => ({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  }));
  // A team group's key ("team-<id>" or "no-team"); empty shows every group.
  const [teamFilter, setTeamFilter] = React.useState("");
  const [draft, setDraft] = React.useState<Record<string, StagedChange>>({});
  // The type a click paints. The picker is always on something, so there is no
  // "no mode" state to guard against; the day shift is the common case.
  const [selectedPlanType, setSelectedPlanType] = React.useState<ConnectProto.ShiftPlanType>(
    PlanType.DAY,
  );
  // Hover is drawn without a React render, which re-rendered every cell and
  // lagged behind the pointer. A driver's rows in the three columns share
  // data-driver, a team's heading and grid block share data-team; the pointer
  // marks every element sharing its row's and its block's id with data-hovered.
  const matrixRef = React.useRef<HTMLDivElement>(null);
  const hovered = React.useRef<Record<string, string | null>>({});
  const markHovered = (target: EventTarget | null) => {
    const root = matrixRef.current;
    for (const attr of ["data-driver", "data-team"]) {
      const el = target instanceof Element ? target.closest(`[${attr}]`) : null;
      const value = el?.getAttribute(attr) ?? null;
      // Same id and still marked: nothing to do. A row React re-created under the
      // pointer (a drop, a refetch) is unmarked, so it is marked again.
      if (!root || (hovered.current[attr] === value && (!el || el.hasAttribute("data-hovered")))) {
        continue;
      }
      hovered.current[attr] = value;
      root
        .querySelectorAll(`[${attr}][data-hovered]`)
        .forEach((el) => el.removeAttribute("data-hovered"));
      if (value) {
        root
          .querySelectorAll(`[${attr}="${value}"]`)
          .forEach((el) => el.setAttribute("data-hovered", ""));
      }
    }
  };
  const openTeamModal = useTeamModal(activeCompanyId ? Number(activeCompanyId) : undefined);

  // The filter sits beside the date row; its chips can make it taller than that
  // row. The grid and the totals then start as much lower, so every row stays
  // level with its driver.
  const filterRef = React.useRef<HTMLDivElement>(null);
  const [filterExtra, setFilterExtra] = React.useState(0);
  React.useEffect(() => {
    const el = filterRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setFilterExtra(Math.max(0, el.offsetHeight - ROW_H)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // Height of the sticky column heads: the date row, the 16px gap, the chips' growth.
  const headH = ROW_H + 16 + filterExtra;
  const datesScrollerRef = React.useRef<HTMLDivElement>(null);

  // The date strip is the one horizontal scroller, its bar right under the days.
  // The grid's team blocks stay put, each framed like a table card, and slide
  // their rows by the strip's scroll, so a narrow window cuts the days inside the
  // frames. The transform is written on each block's rows container (one per
  // team), not through an inherited CSS variable: that would restyle every cell
  // on each scroll event, which Chrome feels.
  const rowsRefs = React.useRef(new Map<string, HTMLDivElement>());
  const slideRows = React.useCallback(() => {
    const transform = `translateX(${-(datesScrollerRef.current?.scrollLeft ?? 0)}px)`;
    rowsRefs.current.forEach((rows) => {
      rows.style.transform = transform;
    });
  }, []);
  // New blocks (data, filter) start at the strip's current scroll.
  React.useLayoutEffect(slideRows);

  // Every driver at once, no list filter: the team grouping below needs the whole list.
  const { data, isFetching } = useListShiftScheduleQuery(
    {
      ownerOrganizationId: activeCompanyId ? BigInt(activeCompanyId) : BigInt(0),
      year,
      month,
    },
    { skip: !activeCompanyId },
  );
  const sortedTeams = useDriverTeams(activeCompanyId);

  const [createShiftPlan, { isLoading: isCreating }] = useCreateShiftPlanMutation();
  const [deleteShiftPlan, { isLoading: isDeleting }] = useDeleteShiftPlanMutation();
  const isSaving = isCreating || isDeleting;

  // The server-computed Asia/Almaty "today" is the single editability
  // boundary — never the browser clock (see ShiftSchedule proto).
  const today = data?.today ?? "";
  const { data: settings } = useGetOrganizationSettingsQuery({}, { skip: !activeCompanyId });
  const [updateSettings] = useUpdateOrganizationSettingsMutation();
  // Set on a drop, per group, so the new order shows before the save comes back.
  const [localOrders, setLocalOrders] = React.useState<Record<string, string[]>>({});
  // Group keys like "no-team" repeat across organizations: drop another one's drops.
  React.useEffect(() => setLocalOrders({}), [activeCompanyId]);
  const savedOrders = React.useMemo(
    () =>
      new Map(
        (settings?.tableColumnsConfig?.tables ?? [])
          .filter((table) => table.tableName.startsWith(DRIVER_ORDER_TABLE))
          .map((table) => [
            table.tableName,
            [...table.columns].sort((a, b) => a.order - b.order).map((c) => c.columnName),
          ]),
      ),
    [settings],
  );
  const orderOf = React.useCallback(
    (groupKey: string) => localOrders[groupKey] ?? savedOrders.get(orderTable(groupKey)) ?? [],
    [localOrders, savedOrders],
  );

  // Listed by name by the server; that name order is also where drivers a saved
  // order does not know yet (new ones) end up.
  const rows = React.useMemo(() => data?.rows ?? [], [data]);

  // A filter on a team that is gone (deleted from its modal, another organization)
  // would hide every row; it falls back to all.
  const activeTeamFilter =
    teamFilter === NO_TEAM || sortedTeams.some((team) => `team-${team.id}` === teamFilter)
      ? teamFilter
      : "";

  // Drivers grouped by team (see groupByTeam), each group in its saved order.
  // Rows are numbered within their group.
  const groups = React.useMemo<Group[]>(
    () =>
      groupByTeam(rows, sortedTeams, driverIdOf, t(Namespace.Common, "noTeam"))
        .filter((group) => !activeTeamFilter || group.key === activeTeamFilter)
        .map(({ items, ...group }) => ({
          ...group,
          entries: orderByIds(items, driverIdOf, orderOf(group.key)).map((row, i) => ({
            key: `${group.key}-${row.driverId}`,
            row,
            number: i + 1,
          })),
        })),
    [rows, sortedTeams, t, orderOf, activeTeamFilter],
  );

  const filterParams = React.useMemo(
    () => [
      {
        key: "team",
        label: t(Namespace.ShiftSchedule, "filter.team"),
        type: "select" as const,
        value: activeTeamFilter,
        options: [
          ...sortedTeams.map((team) => ({ id: `team-${team.id}`, name: team.name })),
          { id: NO_TEAM, name: t(Namespace.Common, "noTeam") },
        ],
      },
      // Shown but disabled until the schedule rows carry the driver's organization.
      {
        key: "subOrganization",
        label: t(Namespace.ShiftSchedule, "filter.subOrganization"),
        type: "select" as const,
        value: "",
        options: [],
        disabled: true,
      },
    ],
    [t, sortedTeams, activeTeamFilter],
  );

  /** A drop within a team moves the driver to the place of the driver it was dropped on. */
  const reorderTeam = (group: Group, from: number, to: number) => {
    if (!settings) return;
    const next = arrayMove(
      group.entries.map((entry) => String(entry.row.driverId)),
      from,
      to,
    );
    const orders = { ...localOrders, [group.key]: next };
    setLocalOrders(orders);

    // `settings` can predate an earlier drop's refetch, so every order dropped
    // here is sent again, not just this one.
    updateSettings({
      id: settings.id,
      tableColumnsConfig: {
        tables: replaceTables(
          settings.tableColumnsConfig?.tables,
          Object.entries(orders).map(([key, ids]) => ({
            tableName: orderTable(key),
            columns: ids.map((columnName, order) => ({ columnName, isHidden: false, order })),
          })),
        ),
      },
    })
      .unwrap()
      .catch((error) => {
        console.error("Failed to save the driver order", error);
        setLocalOrders(({ [group.key]: _failed, ...rest }) => rest);
        toast({ variant: "destructive", title: t(Namespace.ShiftSchedule, "orderSaveError") });
      });
  };

  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const days = React.useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, i) => {
        const date = dateStr(year, month, i + 1);
        const utc = new Date(Date.UTC(year, month - 1, i + 1));
        return {
          day: i + 1,
          date,
          isSunday: utc.getUTCDay() === 0,
          weekdayLabel: utc
            .toLocaleDateString(lang, { weekday: "short", timeZone: "UTC" })
            .replace(/^./, (c) => c.toUpperCase()),
        };
      }),
    [daysInMonth, year, month, lang],
  );

  const prevMonth = shiftMonth(year, month, -1);
  const nextMonth = shiftMonth(year, month, 1);

  const changeMonth = (delta: number) => {
    setMonth(({ year: y, month: m }) => shiftMonth(y, m, delta));
    setDraft({});
  };

  /**
   * A click paints the cell with the picked plan type, staging it for Save.
   * Clicking a cell that already carries that type clears it instead, so a
   * mistake is undone by repeating the click rather than by cycling through
   * the other types.
   *
   * What the cell currently carries is read from the staged edit when there is
   * one and from the server otherwise, so painting works the same before and
   * after a save.
   */
  const toggleCell = (driverId: bigint, date: string, cell?: ConnectProto.ShiftScheduleCell) => {
    const key = `${driverId}:${date}`;
    setDraft((prev) => {
      const staged = prev[key];
      const planned = cell?.state === CellState.PLANNED ? cell : undefined;

      const current =
        staged?.kind === "plan"
          ? staged.planType
          : staged?.kind === "delete"
            ? undefined
            : planned
              ? (planned.planType ?? PlanType.DAY)
              : undefined;

      const draftNext = { ...prev };

      // Already the picked type: the click clears it. With nothing saved to
      // delete the cell simply goes back to untouched.
      if (current === selectedPlanType) {
        if (staged?.kind === "plan") {
          delete draftNext[key];
        } else if (planned?.shiftPlanId) {
          draftNext[key] = { kind: "delete", shiftPlanId: BigInt(planned.shiftPlanId) };
        } else {
          delete draftNext[key];
        }
        return draftNext;
      }

      // Painted back to what the server already has — nothing left to save.
      if (planned && (planned.planType ?? PlanType.DAY) === selectedPlanType) {
        delete draftNext[key];
        return draftNext;
      }

      draftNext[key] = {
        kind: "plan",
        driverId: BigInt(driverId),
        date,
        planType: selectedPlanType,
      };
      return draftNext;
    });
  };

  const draftCount = Object.keys(draft).length;

  const onSave = async () => {
    if (!activeCompanyId || draftCount === 0) return;
    const ownerOrganizationId = BigInt(activeCompanyId);

    const entries = Object.entries(draft);
    const results = await Promise.allSettled(
      entries.map(([, change]) =>
        change.kind === "plan"
          ? createShiftPlan({
              ownerOrganizationId,
              driverId: change.driverId,
              planDate: change.date,
              planType: change.planType,
            }).unwrap()
          : deleteShiftPlan({ id: change.shiftPlanId, ownerOrganizationId }).unwrap(),
      ),
    );

    // Drop only the changes that actually went through, so pressing Save again
    // retries the failures instead of re-running applied creates/deletes.
    // Cells re-toggled while the requests were in flight hold a new object and
    // are left alone.
    setDraft((prev) => {
      const next = { ...prev };
      entries.forEach(([key, change], index) => {
        if (results[index].status === "fulfilled" && next[key] === change) delete next[key];
      });
      return next;
    });

    const failures = results.filter((result) => result.status === "rejected");
    if (failures.length === 0) {
      toast({ title: t(Namespace.ShiftSchedule, "saveSuccess") });
      return;
    }

    console.error(
      "Failed to save shift plans",
      failures.map((failure) => failure.reason),
    );
    toast({ variant: "destructive", title: t(Namespace.ShiftSchedule, "saveError") });
  };

  /**
   * The row's month total with unsaved edits folded in. It cannot be a ±1 per
   * staged change: a day off is not a shift, so retyping night → day off
   * subtracts one while day → night changes nothing. Each staged cell is
   * therefore scored as "counts after" minus "counted before".
   */
  const totalWithDraft = React.useCallback(
    (row: ConnectProto.DriverShiftScheduleRow) => {
      const cellByDate = new Map(row.cells.map((cell) => [cell.date, cell]));
      const delta = Object.entries(draft).reduce((acc, [key, change]) => {
        const [driverId, date] = key.split(":");
        if (driverId !== String(row.driverId)) return acc;

        const before = countsInTotal(cellByDate.get(date)) ? 1 : 0;
        const after = change.kind === "plan" && change.planType !== PlanType.DAY_OFF ? 1 : 0;
        return acc + after - before;
      }, 0);
      return monthTotal(row) + delta;
    },
    [draft],
  );

  // Excel export: driver, one column per day, month total. Unsaved staged
  // edits are deliberately not exported — the file mirrors what is stored.
  const exportColumns = React.useMemo(
    () => [
      {
        id: "driver",
        header: t(Namespace.ShiftSchedule, "export.driver"),
        accessor: (row: ConnectProto.DriverShiftScheduleRow) => row.driverName,
      },
      ...days.map(({ day, date }) => ({
        id: date,
        header: String(day),
        accessor: (row: ConnectProto.DriverShiftScheduleRow) => {
          const cell = row.cells.find((c) => c.date === date);
          // A planned day exports by its type, so the sheet distinguishes a
          // night shift and a day off from a plain planned day.
          const key = !cell
            ? undefined
            : cell.state === CellState.PLANNED
              ? (EXPORT_PLAN_KEY[cell.planType ?? PlanType.DAY] ?? EXPORT_STATE_KEY[cell.state])
              : EXPORT_STATE_KEY[cell.state];
          return key ? t(Namespace.ShiftSchedule, key as never) : "";
        },
      })),
      {
        id: "monthTotal",
        header: t(Namespace.ShiftSchedule, "export.total"),
        accessor: monthTotal,
      },
    ],
    [t, days],
  );

  const { handleExportExcel } = useTableExport({
    data: rows,
    columns: exportColumns,
    reportName: `${t(Namespace.ShiftSchedule, "title")} ${monthLabel(year, month, lang)}`,
  });

  const isEmpty = !isFetching && rows.length === 0;

  return (
    <div className="w-full min-w-0 min-h-0 flex-1 flex flex-col">
      {/* Card — Frame 1000001827: white, 1px #E2E8F0, radius 12 */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl border border-[#e2e8f0] bg-white">
        {/* Matrix and footer are one block spanning the card, so the footer's
            pagination and buttons line up with the table's own edges. */}
        <div className="flex w-full min-h-0 flex-1 flex-col">
          {/* Panel title + month nav. Both stay put; only the matrix scrolls, so
              the nav can't slide away with the days. 64 + 16 = the design's gap
              down to the date row. */}
          <div className="flex flex-none pb-4">
            <div className={cn(NAME_COL, "px-4 pt-4")}>
              <div className="flex h-8 items-center gap-1">
                <IcCalendar className="size-6 text-[#2563eb]" />
                <span className="text-base font-semibold leading-4 tracking-[-0.01px] text-[#242a35]">
                  {t(Namespace.ShiftSchedule, "title")}
                </span>
              </div>
            </div>
            {/* same width as the day grid below, so the month label stays
                centred over the days rather than over the free space */}
            <div
              className="flex h-16 min-w-0 flex-initial items-center justify-between px-4"
              style={{ width: days.length * DAY_COL }}
            >
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="flex items-center gap-2 text-sm font-medium leading-4 tracking-[-0.2px] text-[#242a35]"
              >
                <span className="flex size-8 items-center justify-center rounded p-1 hover:bg-[#f1f5f9]">
                  <IcRightChevron className="size-5 rotate-180" />
                </span>
                {monthLabel(prevMonth.year, prevMonth.month, lang)}
              </button>
              <span className="text-xl font-bold leading-6 tracking-[-0.01px] text-[#242a35]">
                {monthLabel(year, month, lang)}
              </span>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="flex items-center gap-2 text-sm font-medium leading-4 tracking-[-0.2px] text-[#242a35]"
              >
                {monthLabel(nextMonth.year, nextMonth.month, lang)}
                <span className="flex size-8 items-center justify-center rounded p-1 hover:bg-[#f1f5f9]">
                  <IcRightChevron className="size-5" />
                </span>
              </button>
            </div>
            <div className={cn(TOTAL_COL, "shrink-0")} />
          </div>

          {/* Frame 1000001813: one vertical scroll area for all three columns, so a
              row of cells can never drift away from its driver. Each column's head
              (filter, dates, blank) sticks to the top, all three equally tall. The
              31-day grid is wider than most screens, so it scrolls sideways alone. */}
          {/* items-start: each column is as tall as its rows, not the viewport, so
              the grid is not squeezed to the visible height and the heads stay
              stuck all the way down. */}
          <div
            ref={matrixRef}
            onMouseOver={(event) => markHovered(event.target)}
            onMouseLeave={() => markHovered(null)}
            className="flex min-h-0 flex-1 items-start overflow-y-auto overflow-x-hidden"
          >
            {/* Frame 1000001839 — driver panel; never scrolls */}
            <div className={cn(NAME_COL)}>
              {/* level with the date row, then the design's 16px gap; taller with chips */}
              <div className="sticky top-0 z-10 bg-white pb-4">
                <div ref={filterRef} className="flex min-h-10 flex-col justify-center px-4">
                  <FilterBlock
                    compact
                    direction="column"
                    bgColor="white"
                    filterParams={filterParams}
                    onFilterParamsChange={(key: string, value: string | null) => {
                      if (key === "team") setTeamFilter(value ?? "");
                    }}
                  />
                </div>
              </div>

              {/* Item List — px 8, rows 40 */}
              <div className="flex flex-col px-2">
                {isFetching && rows.length === 0
                  ? Array.from({ length: 8 }, (_, i) => (
                      <div key={i} className="flex h-10 items-center px-2">
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))
                  : groups.map((group) => (
                      <div
                        key={group.key}
                        data-testid="schedule-team"
                        data-team={group.key}
                        className="group/team flex flex-col"
                      >
                        {group.label != null && (
                          <div className="flex h-10 shrink-0 items-end px-2 pb-2">
                            <div
                              className={cn(
                                "flex min-w-0 items-center gap-2 text-xs font-bold uppercase leading-4",
                                "text-text-subtle group-hover/team:text-brand-blue group-data-[hovered]/team:text-brand-blue",
                              )}
                            >
                              <span data-testid="schedule-team-label" className="truncate">
                                {group.label}
                              </span>
                              {group.team && (
                                <IconButton
                                  size="xs"
                                  data-testid="schedule-team-settings"
                                  aria-label={t(Namespace.ShiftSchedule, "teamSettings")}
                                  title={t(Namespace.ShiftSchedule, "teamSettings")}
                                  onClick={() => openTeamModal(group.team ?? null)}
                                  className={cn(
                                    "shrink-0 opacity-0 transition-opacity focus-visible:opacity-100",
                                    "group-hover/team:opacity-100 group-data-[hovered]/team:opacity-100",
                                  )}
                                >
                                  <IcSettings className="text-brand-blue" />
                                </IconButton>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col">
                          {/* One list per team: a driver is reordered within its team only. */}
                          <SortableList
                            ids={group.entries.map((entry) => entry.key)}
                            onReorder={(from, to) => reorderTeam(group, from, to)}
                          >
                            {group.entries.map((entry) => (
                              <DriverRow
                                key={entry.key}
                                sortId={entry.key}
                                number={entry.number}
                                name={entry.row.driverName}
                                driverId={String(entry.row.driverId)}
                              />
                            ))}
                          </SortableList>
                        </div>
                      </div>
                    ))}
                {isEmpty && (
                  <p className="p-2 text-sm text-[#667489]">
                    {t(Namespace.ShiftSchedule, "noDrivers")}
                  </p>
                )}
              </div>
            </div>

            {/* Frame 1000001837 — dates and cells slide together horizontally,
                drivers and totals stay put. The scrollbar lives on the date
                strip, so it sits directly under the days. Its width is the
                month's day cells and nothing more — it never grows into the
                free space of a wide (or zoomed-out) window, so the totals
                column stays glued to the last day; on a narrow window it
                shrinks below that and scrolls instead. */}
            <div
              className="flex min-w-0 flex-initial flex-col"
              style={{ width: days.length * DAY_COL }}
            >
              {/* Frame 1000001764 — date cells 40×40. The block is 56 tall (row
                  40 + the design's 16 gap) and owns the visible scrollbar, so the
                  bar renders inside that gap, directly under the days. */}
              <div className="sticky top-0 z-10 bg-white">
                <div
                  ref={datesScrollerRef}
                  onScroll={slideRows}
                  className="h-14 shrink-0 overflow-x-auto"
                >
                  <div className="flex h-10 w-max items-center">
                    {days.map(({ day, date, isSunday, weekdayLabel }) => (
                      <div
                        key={date}
                        className={cn(
                          "flex size-10 shrink-0 flex-col items-center justify-center text-center",
                          isSunday ? "text-text-error" : "text-text-body",
                          // only the calendar dims for past days; the grid does not
                          !!today && date < today && "opacity-70",
                          date === today
                            ? "rounded-lg bg-blue-600 !text-white !opacity-100"
                            : "gap-1 px-2",
                        )}
                      >
                        <span className="text-sm font-medium leading-4 tracking-[-0.2px]">
                          {day}
                        </span>
                        <span className="text-xs font-medium leading-4 tracking-[-0.01px]">
                          {weekdayLabel}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ height: filterExtra }} />
              </div>
              {/* A sideways swipe or shift+wheel over the grid scrolls the date strip. */}
              <div
                onWheel={(event) => {
                  // Shift+wheel can arrive as deltaY; Firefox counts mouse wheels in lines.
                  const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
                  if (delta && datesScrollerRef.current) {
                    datesScrollerRef.current.scrollLeft +=
                      event.deltaMode === WheelEvent.DOM_DELTA_LINE ? delta * 16 : delta;
                  }
                }}
              >
                {/* Graph — radius 8; the outer 1px line comes from the cells
                    themselves, so rows stay flush with the driver list. Each
                    team's rows are their own rounded block, and its heading
                    line is the gap between two blocks. */}
                <div className="bg-white">
                  {groups.map((group) => (
                    <React.Fragment key={group.key}>
                      {group.label != null && <div className="h-10" />}
                      {/* The block clips its cells to its rounded box; `clip`, not
                          `hidden`, so it is no scroll container and the sticky
                          edges inside follow the page's scroll. The rows sit 1px
                          outside the box, so their outer cell borders are clipped
                          and the frame draws the outline. Where the scroll area
                          cuts the block (under the dates, at the bottom), a sticky
                          8px edge draws the cut as a rounded edge too: its white
                          shadow hides the cells outside its corner curves. Fully
                          in view, each edge lies on the frame's own. All of it is
                          CSS, so it moves with the scroll, with no frame of lag. */}
                      <div data-team={group.key} className="relative overflow-clip rounded-lg">
                        <div
                          className="pointer-events-none sticky z-[5] -mb-2 h-2 overflow-hidden"
                          style={{ top: headH }}
                        >
                          <div className="h-full rounded-t-lg border-x border-t border-border-primary shadow-[0_0_0_8px_white]" />
                        </div>
                        {/* slideRows() moves this with the date strip */}
                        <div
                          ref={(el) => {
                            if (el) rowsRefs.current.set(group.key, el);
                            else rowsRefs.current.delete(group.key);
                          }}
                          className="-m-px w-max will-change-transform"
                        >
                          {group.entries.map((entry) => {
                            const { row } = entry;
                            const cellsByDate = new Map(row.cells.map((cell) => [cell.date, cell]));
                            return (
                              <div
                                key={entry.key}
                                data-testid="schedule-grid-row"
                                data-driver={String(row.driverId)}
                                className="flex data-[hovered]:bg-slate-100"
                              >
                                {days.map(({ date }) => {
                                  const cell = cellsByDate.get(date);
                                  const staged = draft[`${row.driverId}:${date}`];
                                  return (
                                    <ScheduleCell
                                      key={date}
                                      cell={cell}
                                      date={date}
                                      driverName={row.driverName}
                                      locale={lang}
                                      editable={!!today && date > today}
                                      staged={stagedEditOf(staged)}
                                      onToggle={() => toggleCell(row.driverId, date, cell)}
                                    />
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                        <div className="pointer-events-none absolute inset-0 rounded-lg border border-border-primary" />
                        <div className="pointer-events-none sticky bottom-0 z-[5] -mt-2 h-2 overflow-hidden">
                          <div className="h-full rounded-b-lg border-x border-b border-border-primary shadow-[0_0_0_8px_white]" />
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Frame 1000001840 — month totals, px 16 */}
            <div className={cn(TOTAL_COL, "shrink-0 px-4")}>
              <div className="sticky top-0 z-10 bg-white" style={{ height: headH }} />
              <div className="flex flex-col">
                {groups.map((group) => (
                  <React.Fragment key={group.key}>
                    {group.label != null && <div className="h-10 shrink-0" />}
                    {group.entries.map((entry) => (
                      <div
                        key={entry.key}
                        data-driver={String(entry.row.driverId)}
                        className="flex h-10 shrink-0 items-center justify-center rounded-lg data-[hovered]:bg-slate-100"
                      >
                        <MonthTotal row={entry.row} total={totalWithDraft(entry.row)} />
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          {/* Frame 1000002204 — footer 72, p 16, items-end */}
          <div className="flex h-[72px] flex-none items-end justify-between p-4">
            {/* keeps the plan picker and the buttons where they were */}
            <div />
            {/* tabs 21081:35617 — grey track, one rounded item per plan type,
                each carrying the colour that plan paints in the matrix. Picking
                one sets what a cell click paints; the picked item takes the
                design's surface/action fill. The design's settings button sits
                32px to the right of this; left out until it has something to
                open. */}
            <div role="radiogroup" className="flex h-8 items-center rounded-xl bg-[#e2e8f0]">
              {PLAN_TYPES.map((planType) => {
                const picked = planType === selectedPlanType;
                return (
                  <button
                    key={planType}
                    type="button"
                    role="radio"
                    aria-checked={picked}
                    onClick={() => setSelectedPlanType(planType)}
                    className={cn(
                      "flex h-full min-w-[72px] items-center justify-center gap-1 rounded-lg px-2 py-1 drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)]",
                      picked && "bg-[#2563eb]",
                    )}
                  >
                    <span className={cn("size-4 shrink-0 rounded", planFill(planType))} />
                    <span
                      className={cn(
                        "whitespace-nowrap text-center text-xs font-medium leading-4 tracking-[-0.01em]",
                        picked ? "text-white" : "text-[#242a35]",
                      )}
                    >
                      {t(Namespace.ShiftSchedule, PLAN_LEGEND_KEY[planType] as never)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-end gap-2">
              <Button
                id="shift-schedule-download-button"
                variant="excel"
                size="sm"
                onClick={handleExportExcel}
              >
                {t(Namespace.ShiftSchedule, "download")}
              </Button>
              <Button
                id="shift-schedule-save-button"
                variant="solidBlue"
                size="sm"
                disabled={isSaving}
                onClick={onSave}
              >
                {t(Namespace.ShiftSchedule, "save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftSchedulePage;
